import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { BaseWidget } from "./BaseWidget";
import { PersonaFieldsControl } from "../controls/PersonaFieldsControl";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import templatePersonaNotice from "./_shared/templatePersonaNotice";
import NO_PERSONA_TEXT from "./_shared/noPersonaText";
import usePersonaProfile from "./_shared/usePersonaProfile";
import usePublicPersona from "./_shared/usePublicPersona";
import { publicProfileState } from "../../../../helpers/liveReportHelpers/publicPersonaState";
import personaProfileRows from "../../../../helpers/liveReportHelpers/personaProfileRows";

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T9)
// 'Traject en gegevens' van de deelnemer van het rapport: alleen de velden die
// de bouwer koos (Settings.fields, sleutels uit personaWidgetTypes.js), in die
// volgorde. Type in de registry: PERSONA_WIDGET_TYPES.PROFILE — de server
// (functions/liveReports/publicPersonaData.js) levert de link hierop.
//
// Lezen en tonen zijn gescheiden: PersonaProfileView krijgt `status` +
// `profile`, uit usePersonaProfile in de app of uit `personaData` op de link.

/**
 * @extends {BaseWidget}
 */
export class PersonaProfileWidget extends BaseWidget {

    getName() { return 'Traject en gegevens'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <BadgeOutlinedIcon />; }

    getControls() {
        return {
            'fields': new PersonaFieldsControl({
                label: 'Velden',
                description: 'Kies welke gegevens van de deelnemer de widget toont, in deze volgorde.',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {import('./_shared/usePersonaProfile').PersonaProfileStatus} props.status - Stand van de lezing
 * @param {Array<string>} props.fields - Gekozen sleutels, in volgorde
 * @param {Object<string, *>} props.profile - Waarde per sleutel; lege velden ontbreken
 * @returns {JSX.Element}
 */
export const PersonaProfileView = ({ status, fields, profile }) => {
    // Op de rijen, niet op Settings.fields: een oude keuze met alleen een
    // identificatienummer (BSN) heeft geen rij en zou anders een leeg vak geven.
    if (personaProfileRows(fields, {}).length === 0) {
        return <p className="persona-profile-message">Kies in de instellingen welke gegevens van de deelnemer je wilt tonen.</p>;
    }
    if (status === 'idle') {
        return <p className="persona-profile-message">{NO_PERSONA_TEXT}</p>;
    }
    if (status === 'error') {
        return <p role="alert" className="persona-profile-message">De gegevens van deze deelnemer konden niet worden geladen.</p>;
    }
    if (status === 'missing') {
        return <p className="persona-profile-message">Deze deelnemer bestaat niet meer</p>;
    }
    if (status !== 'ready') {
        return <p className="persona-profile-message">Gegevens van de deelnemer laden…</p>;
    }

    return (
        <dl className="persona-profile-list">
            {personaProfileRows(fields, profile).map((row) => (
                <div key={row.key} className="persona-profile-row">
                    <dt className="persona-profile-label" data-testid="persona-profile-label">{row.label}</dt>
                    {row.isEmpty
                        ? <dd className="persona-profile-value persona-profile-value--empty">Niet ingevuld</dd>
                        : <dd className="persona-profile-value">{row.value}</dd>}
                </div>
            ))}
        </dl>
    );
};

/**
 * @param {object} props
 * @param {Array<string>} [props.fields] - Settings.fields
 * @returns {JSX.Element}
 */
const Renderer = ({ fields }) => {
    const { isPublic, personaData, personasModuleOn, personaId, templateStatus } = usePublicPersona();
    const chosen = Array.isArray(fields) ? fields : [];
    // Op de link en in een template leest de hook niets (`null`); het profiel komt van de server.
    const live = usePersonaProfile(isPublic ? null : personaId, chosen);
    const { status, profile } = isPublic && personaId ? publicProfileState(personaData) : live;

    const notice = templatePersonaNotice(templateStatus);
    if (notice) return notice;
    if (!personasModuleOn) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Deelnemers" />;
    }
    return <PersonaProfileView status={status} fields={chosen} profile={profile} />;
};
